import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { PageHeader } from '@features/property-management/components/PageHeader';
import { navIconFor } from '@layout/agency/agency-nav';
import { applyHistoryFilters } from '@/shared/utils/partnerships-history-filters';
import type { GeoGroup } from '../components/SearchHistoryByGeo';
import {
  PartnershipsHistoryProvider,
  usePartnershipsHistory,
} from '../context/PartnershipsHistoryContext';
import { SearchHistoryByGeo } from '../components/SearchHistoryByGeo';
import { HistoryFilterBar } from '../components/HistoryFilterBar';
import { SearchLimitBanner } from '../components/SearchLimitBanner';
import { useAgencyId } from '../hooks/useAgencyId';

/**
 * Renders the partnerships search-history view: filter bar plus geo-grouped results.
 */
function HistoryContent() {
  const { t } = useTranslation('features/partnerships');
  const navigate = useNavigate();
  const agencyId = useAgencyId();

  const groups = usePartnershipsHistory((s) => s.groups);
  const loading = usePartnershipsHistory((s) => s.loading);
  const error = usePartnershipsHistory((s) => s.error);
  const filters = usePartnershipsHistory((s) => s.filters);
  const setFilters = usePartnershipsHistory((s) => s.setFilters);
  const loadSearches = usePartnershipsHistory((s) => s.loadSearches);

  useEffect(() => {
    if (agencyId) loadSearches(agencyId);
  }, [agencyId, loadSearches]);

  const filteredGroups = applyHistoryFilters(groups as any, filters) as unknown as GeoGroup[];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="px-(--space-6) pt-(--space-6) pb-(--space-3)">
        <PageHeader
          title={t('page_title')}
          icon={navIconFor('partnerships')}
          ctaLabel={t('history.new_search')}
          searchPlaceholder={t('wizard.search_placeholder')}
          searchValue={filters.text}
          onCtaClick={() => navigate('new')}
          onSearchChange={(val) => setFilters({ text: val })}
          hideFilters
          introLabel={t('intro.label')}
          introBody={t('intro.body')}
          introTestIdPrefix="partnerships"
        />
      </div>

      <div className="flex flex-col gap-[var(--space-4)] px-(--space-6) pb-(--space-6)">
        <SearchLimitBanner />
        <HistoryFilterBar filters={filters} onChange={setFilters} />
        <SearchHistoryByGeo
          groups={filteredGroups}
          loading={loading}
          error={error}
          onSelect={(id) => navigate(id)}
        />
      </div>
    </div>
  );
}

export default function PartnershipsHistoryPage() {
  return (
    <PartnershipsHistoryProvider>
      <HistoryContent />
    </PartnershipsHistoryProvider>
  );
}
